'use client'

import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import type { Post } from '@/payload-types'
import { ArticleView } from './ArticleView'
import { loadStreamPost } from '@/lib/streamActions'

/**
 * Scroll infinito «continuous reading» dei correlati. Sotto l'articolo SSR (renderizzato
 * da page.tsx) accoda gli articoli della coda `queue` in versione INTEGRALE man mano che
 * l'utente scorre, e — quando un articolo entra al centro del viewport — aggiorna l'URL e
 * spara un page_view GA4. L'URL cambia con `history.replaceState` (preserva lo state del
 * router Next, nessuna voce extra in cronologia).
 *
 * `children` = fallback SSR (le card dei correlati): è nell'HTML iniziale per SEO/no-JS e
 * viene sostituito dallo stream solo dopo l'idratazione (progressive enhancement).
 *
 * NB GA4: il page_view è emesso a mano via gtag. Per evitare il DOPPIO conteggio va
 * disattivato «page changes based on browser history events» nell'Enhanced Measurement.
 */
export function ArticleStream({
  slugs,
  siteName,
  siteUrl,
  children,
  footer,
}: {
  slugs: string[]
  siteName: string
  /** Origin assoluto del tenant, propagato agli articoli streamati per i tasti social. */
  siteUrl?: string
  children?: ReactNode
  /** Coda mostrata alla fine dello stream (altri articoli + categorie suggerite). */
  footer?: ReactNode
}) {
  const [enhanced, setEnhanced] = useState(false)
  const [items, setItems] = useState<Post[]>([])
  const [loading, setLoading] = useState(false)
  const [done, setDone] = useState(slugs.length === 0)

  const cursorRef = useRef(0) // prossimo indice della coda da caricare
  const loadingRef = useRef(false) // guardia anti-rientro (sincrona)
  const sentinelRef = useRef<HTMLDivElement>(null)
  const sentinelObsRef = useRef<IntersectionObserver | null>(null)
  const activeObsRef = useRef<IntersectionObserver | null>(null)
  const activeSlugRef = useRef<string | null>(null)

  // Progressive enhancement: lo stream parte solo dopo l'idratazione.
  useEffect(() => setEnhanced(true), [])

  // Rileva l'articolo «attivo» (quello che attraversa il centro del viewport) →
  // cambia URL + document.title + page_view GA4. Salta il primo (articolo SSR già contato).
  const activate = useCallback((el: HTMLElement) => {
    const slug = el.dataset.articleSlug
    if (!slug || slug === activeSlugRef.current) return
    const href = el.dataset.articleHref ?? `/${slug}/`
    const title = el.dataset.articleTitle ?? document.title
    activeSlugRef.current = slug
    window.history.replaceState(window.history.state, '', href)
    document.title = title
    const w = window as unknown as { gtag?: (...args: unknown[]) => void }
    w.gtag?.('event', 'page_view', {
      page_title: title,
      page_location: window.location.origin + href,
    })
  }, [])

  // Callback ref: osserva ogni articolo streamato appena montato. Osserva l'<article>
  // INTERNO (porta i data-article-*), non il wrapper — altrimenti activate() leggerebbe
  // slug undefined e l'URL non cambierebbe.
  const observeArticle = useCallback((node: HTMLElement | null) => {
    if (!node) return
    const el = node.querySelector<HTMLElement>('[data-article-slug]') ?? node
    activeObsRef.current?.observe(el)
  }, [])

  // Observer «articolo attivo»: linea sottile al centro del viewport (un solo articolo
  // la attraversa per volta). Osserva da subito l'articolo SSR + eventuali già montati.
  useEffect(() => {
    if (!enhanced) return
    const io = new IntersectionObserver(
      (entries) => {
        for (const e of entries) if (e.isIntersecting) activate(e.target as HTMLElement)
      },
      { rootMargin: '-45% 0px -45% 0px', threshold: 0 },
    )
    activeObsRef.current = io
    const existing = document.querySelectorAll<HTMLElement>('[data-article-slug]')
    if (existing[0]) activeSlugRef.current = existing[0].dataset.articleSlug ?? null // attivo iniziale = SSR (no fire)
    existing.forEach((el) => io.observe(el))
    return () => {
      io.disconnect()
      activeObsRef.current = null
    }
  }, [enhanced, activate])

  // Carica il prossimo articolo della coda.
  const loadNext = useCallback(async () => {
    if (loadingRef.current || cursorRef.current >= slugs.length) return
    const idx = cursorRef.current
    loadingRef.current = true
    setLoading(true)
    let post: Post | null = null
    try {
      post = await loadStreamPost(slugs[idx])
    } catch {
      post = null
    }
    cursorRef.current = idx + 1
    if (post) setItems((prev) => [...prev, post as Post])
    loadingRef.current = false
    setLoading(false)
    if (cursorRef.current >= slugs.length) {
      setDone(true)
    } else {
      // Articolo corto / viewport alto: forza un re-check della sentinel (l'IO non
      // riemette se l'intersezione non cambia) così la catena prosegue senza scroll.
      requestAnimationFrame(() => {
        const io = sentinelObsRef.current
        const s = sentinelRef.current
        if (io && s) {
          io.unobserve(s)
          io.observe(s)
        }
      })
    }
  }, [slugs])

  // Observer sentinella: carica in anticipo (600px) prima di toccare il fondo.
  useEffect(() => {
    if (!enhanced || done) return
    const sentinel = sentinelRef.current
    if (!sentinel) return
    const io = new IntersectionObserver(
      (entries) => {
        if (entries[0]?.isIntersecting) void loadNext()
      },
      { rootMargin: '600px 0px' },
    )
    sentinelObsRef.current = io
    io.observe(sentinel)
    return () => {
      io.disconnect()
      sentinelObsRef.current = null
    }
  }, [enhanced, done, loadNext])

  // Prima dell'idratazione (e per i crawler / no-JS): fallback SSR con le card correlate
  // + la coda (altri articoli della categoria + categorie suggerite).
  if (!enhanced)
    return (
      <>
        {children}
        {footer}
      </>
    )

  return (
    <div>
      {items.map((post) => (
        <div key={post.id} ref={observeArticle} className="border-t-8 border-zinc-100">
          <ArticleView post={post} siteName={siteName} siteUrl={siteUrl} />
        </div>
      ))}

      {!done ? (
        <>
          <div ref={sentinelRef} aria-hidden className="h-px" />
          {loading ? (
            <div className="container-wide py-16" aria-live="polite" aria-busy="true">
              <span className="sr-only">Caricamento articolo successivo…</span>
              <div className="mx-auto max-w-3xl animate-pulse space-y-4">
                <div className="aspect-[2.4/1] w-full rounded-card bg-zinc-100" />
                <div className="h-8 w-3/4 rounded bg-zinc-100" />
                <div className="h-4 w-full rounded bg-zinc-100" />
                <div className="h-4 w-5/6 rounded bg-zinc-100" />
              </div>
            </div>
          ) : null}
        </>
      ) : (
        // Stream esaurito: accoda la coda dopo l'ultimo articolo letto.
        footer
      )}
    </div>
  )
}
